import React, { useEffect, useRef, useState } from 'react';
import styled, { x } from '@xstyled/styled-components';
import { useSequenceStore } from '~/training/sequence/SequenceStore';
import { select } from '~/lib/select';
import { SpeakerIcon } from '../audio-popup/components/SpeakerIcon';
import { TickMarkIcon } from '../audio-popup/components/TickMarkIcon';
import { sanitizeHtmlForDisplay } from '~/lib/sanitization';

const ButtonWrap = styled.button<{ size: number; completed: boolean }>`
  position: relative;
  width: ${p => p.size}px;
  height: ${p => p.size}px;
  border-radius: 50%;
  border: 0;
  cursor: pointer;
  background: #ffffffee;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  color: #111;
  outline: none;
  transition:
    background 0.2s ease,
    transform 0.15s ease;
  &:hover {
    background: #fff;
  }
  &:active {
    transform: scale(0.94);
  }
  svg path {
    // fill: currentColor;
  }
  /* Removed green background on completion; keep icon color change optional */
  ${p => p.completed && 'color:#1b7a38;'}
`;

const Popover = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translate(-50%, 12px);
  width: 359px;
  max-width: 95vw;
  height: 246px;
  max-height: 80vh;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
  padding: 18px 22px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  z-index: 40;
`;

const ProgressBar = styled.div`
  height: 6px;
  width: 100%;
  background: #eceef2;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
  &:after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: var(--p, 0%);
    background: #9738ff;
    transition: width 0.15s linear;
  }
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  button {
    padding: 10px 16px;
    font-family: Raleway;
    font-size: 14px;
    font-weight: 600;
    border-radius: 8px;
    cursor: pointer;
    border: 0;
  }
  .primary {
    background: #9738ff;
    color: #fff;
  }
  .secondary {
    background: #f3ecff;
    color: #9738ff;
  }
`;

export interface StandaloneCardAudioPopupProps {
  mediaUrl: string;
  name?: string;
  body?: string;
  replay?: boolean;
  btnSize?: number;
  showPopover?: boolean; // when false, no popover/subtitles displayed
  onComplete?: () => void;
  onRef?: (api: { stop: () => void }) => void; // allow parent to stop audio externally
}

export const StandaloneCardAudioPopup: React.FC<
  StandaloneCardAudioPopupProps
> = ({
  mediaUrl,
  name,
  body,
  replay = true,
  btnSize = 64,
  showPopover = true,
  onComplete,
  onRef
}) => {
  const { setAudioPlayed } = useSequenceStore(s => select(s, 'setAudioPlayed'));
  const [progress, setProgress] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [open, setOpen] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // ring removed

  useEffect(() => {
    if (!audioRef.current) {
      const el = new Audio(mediaUrl);
      el.preload = 'auto';
      el.addEventListener('timeupdate', () => {
        setProgress(el.currentTime / (el.duration || 1));
      });
      el.addEventListener('ended', () => {
        setPlaying(false);
        setProgress(1);
        setCompleted(true);
        setAudioPlayed(mediaUrl);
        onComplete?.();
        if (!replay) setOpen(false);
      });
      audioRef.current = el;
    }
    onRef?.({
      stop: () => {
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
          setPlaying(false);
        }
      }
    });
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, [mediaUrl, replay, onComplete, onRef]);

  const toggle = () => {
    if (!audioRef.current) return;
    if (audioRef.current.paused) {
      audioRef.current.play().catch(() => {
        // Ignored: play() can reject due to autoplay policy or rapid toggle; safe to ignore.
      });
      setPlaying(true);
    } else {
      audioRef.current.pause();
      setPlaying(false);
    }
  };

  const clickMain = () => {
    if (!open) setOpen(true);
    if (!playing) toggle();
  };

  return (
    <x.div position="relative" display="inline-flex" zIndex={5}>
      <ButtonWrap
        type="button"
        aria-label={playing ? 'Pause audio' : 'Play audio'}
        onClick={clickMain}
        size={btnSize}
        completed={completed}
        style={{ minWidth: btnSize, minHeight: btnSize }}
      >
        {completed ? <TickMarkIcon /> : <SpeakerIcon />}
      </ButtonWrap>
      {open && showPopover && (
        <Popover
          role="dialog"
          aria-label={name || 'Audio details'}
          style={{ top: 'calc(100% + 8px)' }}
        >
          <x.h3
            fontSize="16px"
            m={0}
            dangerouslySetInnerHTML={{
              __html: sanitizeHtmlForDisplay(name)
            }}
          />
          <x.div
            flex={1}
            overflow="auto"
            fontSize="14px"
            dangerouslySetInnerHTML={{
              __html: sanitizeHtmlForDisplay(body)
            }}
          />
          <ProgressBar
            style={{ ['--p' as string]: `${Math.round(progress * 100)}%` }}
          />
          <ControlsRow>
            <button className="primary" type="button" onClick={toggle}>
              {playing
                ? 'Pause'
                : completed
                  ? replay
                    ? 'Replay'
                    : 'Replay Disabled'
                  : 'Play'}
            </button>
            <button
              className="secondary"
              type="button"
              onClick={() => {
                if (audioRef.current) {
                  audioRef.current.currentTime = 0;
                  audioRef.current.pause();
                }
                setProgress(0);
                setPlaying(false);
                setCompleted(false);
              }}
              disabled={!completed && progress === 0}
            >
              Reset
            </button>
            <button
              className="secondary"
              type="button"
              onClick={() => setOpen(false)}
            >
              Close
            </button>
          </ControlsRow>
        </Popover>
      )}
    </x.div>
  );
};
